<template>
  <div :class="`${filterBarClasses} tw:rounded-t-md`">
    <div :class="filterBarWrapperClasses">
      <div :class="filterBarBasicFiltersClasses">
        <slot />
      </div>
      <ks-collapse :visible="showAdvancedFilters">
        <div :class="filterBarAdvancedFiltersClasses">
          <slot name="advanced" />
        </div>
      </ks-collapse>
    </div>
    <div
      v-if="advancedFiltersAvailable"
      class="filter-bar__advanced-button clickable tw:text-sm"
      @click="$toggleAdvancedFilters"
    >
      {{ buttonText }}
      <div
        v-if="advancedFiltersDirty && !advancedFiltersVisible"
        class="filter-bar__advanced-filters-dirty-indicator"
        v-tooltip="'There are still some active Advanced Filters'"
      ></div>
    </div>
  </div>
</template>

<script>
  import { buildClassString } from 'Utils'
  import KsCollapse from 'Components/KsCollapse.vue'

  export default {
    name: 'filter-bar',
    components: {
      KsCollapse,
    },
    data () {
      return {
        advancedFiltersVisible: false,
        baseFilterBarClass: 'filter-bar',
      }
    },
    computed: {
      advancedFiltersAvailable () {
        return !!this.$slots['advanced']
      },
      buttonText () {
        return `${this.advancedFiltersVisible ? 'Hide' : 'Show'} Advanced Filters`
      },
      filterBarClasses () {
        return buildClassString(this.baseFilterBarClass, [
          {
            className: 'grid',
            testCondition: this.useGrid,
            bem: true,
          },
        ])
      },
      filterBarWrapperClasses () {
        const baseClass = `${this.baseFilterBarClass}__field-wrapper`
        return buildClassString(baseClass, [
          {
            className: 'grid',
            testCondition: this.useGrid,
            bem: true,
          },
        ])
      },
      filterBarBasicFiltersClasses () {
        const baseClass = `${this.baseFilterBarClass}__basic-filters`
        return buildClassString(baseClass, [
          {
            className: 'grid',
            testCondition: this.useGrid,
            bem: true,
          },
        ])
      },
      filterBarAdvancedFiltersClasses () {
        const baseClass = `${this.baseFilterBarClass}__advanced-filters`
        return buildClassString(baseClass, [
          {
            className: 'grid',
            testCondition: this.useGrid,
            bem: true,
          },
        ])
      },
      showAdvancedFilters () {
        return this.advancedFiltersAvailable && this.advancedFiltersVisible
      },
    },
    methods: {
      $hideAdvancedFilters () {
        this.advancedFiltersVisible = false
        this.$emit('toggle-advanced-filters', this.advancedFiltersVisible)
      },
      $showAdvancedFilters () {
        this.advancedFiltersVisible = true
        this.$emit('toggle-advanced-filters', this.advancedFiltersVisible)
      },
      $toggleAdvancedFilters () {
        this.advancedFiltersVisible = !this.advancedFiltersVisible
        this.$emit('toggle-advanced-filters', this.advancedFiltersVisible)
      },
    },
    props: {
      // Used to display the "dirty" indicator if advanced filters are hidden
      advancedFiltersDirty: {
        type: Boolean,
        default: false,
      },
      useGrid: {
        type: Boolean,
        default: false,
      },
    },
  }
</script>

<style lang="scss">
@use "sass:list";
@use "~Styles/colors";
@use "~Styles/variables";
@use "~Styles/mixins";

$filter-bar-height: 50px;
$filter-input-spacing: 15px;

.filter-bar {
  $p: &;

  background-color: colors.$kommander-white;
  border-bottom: solid 1px colors.$kommander-dark-border-color;
  margin-bottom: variables.$content__main__content-padding;
  margin-top: variables.$content__main__content-padding * -1;
  margin-left: variables.$content__main__content-padding * -1;
  margin-right: variables.$content__main__content-padding * -1;
  display: flex;
  flex-wrap: wrap;

  &__field-wrapper {
    flex: 1;
    padding-top: 5px;
    padding-bottom: 5px;
    padding-right: $filter-input-spacing;

    &--grid {
      padding-left: $filter-input-spacing;
    }
  }

  &__filter-field {
    flex: 1;

    &:not(&--grid) {
      padding-left: $filter-input-spacing;
    }
  }

  &__filter-pair {
    display: flex;
    flex: 1;
    flex-basis: 380px;
    align-items: flex-end;
    flex-wrap: wrap;
    grid-column: span 2;

    &--divided {
      #{$p}__filter-field:first-child .form__input,
      #{$p}__filter-field:first-child .form__input-group__btn:last-child {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }

      #{$p}__filter-field:last-child {
        padding-left: 0;

        .form__input {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }
      }
    }
  }

  &__filter-divider {
    display: flex;
    font-size: 16px;
    justify-content: center;
    align-content: center;
    padding: variables.$form-input-padding;
    background-color: colors.$form-addon-background-color;
    border: variables.$form-border-style variables.$form-border-color 0;
    border-top-width: variables.$form-border-width;
    border-bottom-width: variables.$form-border-width;
    margin-bottom: variables.$form-padding-bottom;

    &-inline {
      margin-bottom: variables.$form-padding-bottom-inline;
    }
  }

  &__advanced-button {
    align-items: center;
    background-color: colors.$kommander-light-gray;
    border-top: solid colors.$kommander-dark-border-color 1px;
    display: flex;
    justify-content: center;
    padding: 5px;
    width: 100%;

    &:hover {
      background-color: colors.$kommander-hover-bg;
    }

    @media (max-width: variables.$media-sm-width - 1px) {
      padding: 15px;
    }
  }

  &__basic-filters,
  &__advanced-filters {
    display: flex;
    flex-wrap: wrap;

    &--grid {
      display: grid;
      grid-auto-columns: 1fr;
      grid-auto-flow: column;
      grid-column-gap: $filter-input-spacing;

      @media screen and (max-width: variables.$media-lg-width - 1px) and (min-width: variables.$media-md-width) {
        grid-auto-flow: initial;
        grid-template-columns: repeat(5, 1fr);
      }

      @media screen and (max-width: variables.$media-md-width - 1px) and (min-width: variables.$media-sm-width) {
        grid-auto-flow: initial;
        grid-template-columns: repeat(4, 1fr);
      }

      @media screen and (max-width: variables.$media-sm-width - 1px) {
        grid-auto-flow: initial;
        grid-template-columns: repeat(2, 1fr);
      }
    }
  }

  &__advanced-filters-dirty-indicator {
    @include mixins.circle(12px, list.nth(colors.$kommander-reds, 2));
    display: inline-block;
    margin-left: 5px;
  }

  .btn,
  .btn:active {
    margin: 7px 1px;
  }
}
</style>
